import { FC, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  X,
  Home,
  Briefcase,
  Calendar,
  LogIn,
  UserPlus,
  LayoutDashboard,
  User,
  Settings,
  LogOut,
  Monitor,
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useAuth } from '../../../contexts/AuthContext';
import { ThemeSwitcher } from '../../theme';

export interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

interface NavLink {
  to: string;
  label: string;
  icon: React.ReactNode;
}

const publicLinks: NavLink[] = [
  { to: '/', label: 'Home', icon: <Home className="w-5 h-5" /> },
  { to: '/services', label: 'Services', icon: <Briefcase className="w-5 h-5" /> },
  { to: '/book', label: 'Book Now', icon: <Calendar className="w-5 h-5" /> },
];

/**
 * MobileMenu component - Slide-out mobile navigation
 * Full screen overlay with navigation links
 */
export const MobileMenu: FC<MobileMenuProps> = ({ isOpen, onClose, className }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Close menu on route change
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleLogout = () => {
    logout();
    onClose();
    navigate('/');
  };

  // Get dashboard link based on user role
  const getDashboardLink = (): NavLink | null => {
    if (!user) return null;

    switch (user.role) {
      case 'ADMIN':
      case 'SUPER_ADMIN':
        return {
          to: '/admin',
          label: 'Admin Dashboard',
          icon: <LayoutDashboard className="w-5 h-5" />,
        };
      case 'THERAPIST':
        return {
          to: '/therapist/dashboard',
          label: 'Therapist Dashboard',
          icon: <LayoutDashboard className="w-5 h-5" />,
        };
      default:
        return {
          to: '/dashboard',
          label: 'Dashboard',
          icon: <LayoutDashboard className="w-5 h-5" />,
        };
    }
  };

  const dashboardLink = getDashboardLink();

  const isActiveLink = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/50 dark:bg-black/70 z-40 transition-opacity duration-300 lg:hidden',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Menu panel */}
      <div
        className={cn(
          'fixed top-0 right-0 bottom-0 w-80 max-w-full bg-white dark:bg-slate-900 z-50',
          'transform transition-transform duration-300 ease-out lg:hidden',
          'shadow-2xl',
          isOpen ? 'translate-x-0' : 'translate-x-full',
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-16 border-b border-gray-100 dark:border-slate-800">
          <span className="text-xl font-bold text-gray-900 dark:text-slate-100">
            Massa<span className="text-primary-600">therapy</span>
          </span>
          <button
            type="button"
            onClick={onClose}
            className={cn(
              'p-2 rounded-lg text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200',
              'hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary-500'
            )}
            aria-label="Close menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* User info (if authenticated) */}
        {isAuthenticated && user && (
          <div className="px-4 py-4 bg-gray-50 dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center',
                  'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 font-medium'
                )}
              >
                {user.name
                  .split(' ')
                  .map((part) => part.charAt(0))
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                  {user.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-slate-400 truncate">{user.email}</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation links */}
        <nav className="flex-1 overflow-y-auto py-4">
          {/* Public links */}
          <div className="px-4 space-y-1">
            {publicLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg',
                  'text-base font-medium transition-colors',
                  isActiveLink(link.to)
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800'
                )}
              >
                {link.icon}
                {link.label}
              </Link>
            ))}
          </div>

          {/* Authenticated user links */}
          {isAuthenticated && user && (
            <>
              <div className="mx-4 my-4 border-t border-gray-200 dark:border-slate-700" />
              <div className="px-4 space-y-1">
                {/* Dashboard */}
                {dashboardLink && (
                  <Link
                    to={dashboardLink.to}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 rounded-lg',
                      'text-base font-medium transition-colors',
                      isActiveLink(dashboardLink.to)
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800'
                    )}
                  >
                    {dashboardLink.icon}
                    {dashboardLink.label}
                  </Link>
                )}

                {/* Booking History - for customers */}
                {user.role === 'CUSTOMER' && (
                  <Link
                    to="/dashboard?section=history"
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 rounded-lg',
                      'text-base font-medium transition-colors',
                      isActiveLink('/dashboard')
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800'
                    )}
                  >
                    <Calendar className="w-5 h-5" />
                    Booking History
                  </Link>
                )}

                {/* Active Devices - login session management for customers */}
                {user.role === 'CUSTOMER' && (
                  <Link
                    to="/sessions"
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 rounded-lg',
                      'text-base font-medium transition-colors',
                      isActiveLink('/sessions')
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800'
                    )}
                  >
                    <Monitor className="w-5 h-5" />
                    Active Devices
                  </Link>
                )}

                {/* Profile */}
                <Link
                  to="/profile"
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-lg',
                    'text-base font-medium transition-colors',
                    isActiveLink('/profile')
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800'
                  )}
                >
                  <User className="w-5 h-5" />
                  Profile
                </Link>

                {/* Settings */}
                <Link
                  to="/settings"
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-lg',
                    'text-base font-medium transition-colors',
                    isActiveLink('/settings')
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800'
                  )}
                >
                  <Settings className="w-5 h-5" />
                  Settings
                </Link>
              </div>
            </>
          )}
        </nav>

        {/* Footer actions */}
        <div className="border-t border-gray-100 dark:border-slate-800 p-4">
          {/* Theme Switcher */}
          <div className="mb-4">
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-2">Theme</p>
            <ThemeSwitcher />
          </div>

          {isAuthenticated ? (
            <button
              type="button"
              onClick={handleLogout}
              className={cn(
                'flex items-center justify-center gap-2 w-full',
                'px-4 py-3 rounded-lg',
                'text-base font-medium text-red-600 dark:text-red-400',
                'bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors'
              )}
            >
              <LogOut className="w-5 h-5" />
              Sign out
            </button>
          ) : (
            <div className="space-y-2">
              <Link
                to="/login"
                className={cn(
                  'flex items-center justify-center gap-2 w-full',
                  'px-4 py-3 rounded-lg',
                  'text-base font-medium text-gray-700 dark:text-slate-300',
                  'border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors'
                )}
              >
                <LogIn className="w-5 h-5" />
                Sign in
              </Link>
              <Link
                to="/register"
                className={cn(
                  'flex items-center justify-center gap-2 w-full',
                  'px-4 py-3 rounded-lg',
                  'text-base font-medium text-white',
                  'bg-primary-600 hover:bg-primary-700 transition-colors'
                )}
              >
                <UserPlus className="w-5 h-5" />
                Create account
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

MobileMenu.displayName = 'MobileMenu';
